import { html, unsafeCSS } from 'lit';
import { property, state, customElement } from 'lit/decorators.js';
import { BEACON_MODE_CLASSNAMES } from '@trp-dms-digital-shared/ds-ui-library-common';

import { PopBaseComponent } from '../../base/pop-base-component';
import { Constants as SummaryConstants } from '../summary/summary.constants';
import { Constants as SummarySearchConstants } from '../summary-search/summary-search.constants';
import { Constants } from './sticky-header.constants';
import style from './sticky-header.scss?inline';

/**
 * @tag pop-summary-sticky-header-component
 * @attribute {string} title - header title content
 */
@customElement(Constants.TAG_NAME)
export class PopSummaryStickyHeader extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  /**
   * @internal
   */
  tagName = Constants.TAG_NAME;

  @property({ attribute: 'data-title' })
  title;
  @state() showTitleAndSearch = false;
  @state() tabBarPadding: string = Constants.ZERO_PX;

  constructor() {
    super();
  }

  renderTitleAndSearch() {
    return html`
      <p
        class="sticky-title b-typography-serif--headline--100 b-lg:serif--headline--200"
      >
        ${this.title}
      </p>
      <div class="summary-search-container"></div>
    `;
  }

  firstUpdated(): void {
    window.addEventListener(Constants.SCROLL, this.handleScroll.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(Constants.SCROLL, this.handleScroll.bind(this));
  }

  /*
   * This function adds appropriate padding to the beacon navigation when the sticky header is shown
   */
  //
  applyBeaconNavigationPadding() {
    const beaconAnchorNav = this.querySelector(
      Constants.BEACON_ANCHOR_NAVIGATION,
    );

    if (beaconAnchorNav) {
      const tabElements = beaconAnchorNav.shadowRoot?.querySelector(
        Constants.BEACON_ANCHOR_NAVIGATION_TAB_BAR,
      );
      if (tabElements && tabElements instanceof HTMLElement) {
        tabElements.style.paddingTop = this.tabBarPadding;
      }
    }
  }

  /*
   * This function removed the beacon mode classes from the search element
   */
  removeBeaconModeClasses(element: HTMLElement) {
    BEACON_MODE_CLASSNAMES.forEach((classname) => {
      element.shadowRoot?.children[0]?.classList.remove(classname);
    });
  }

  /*
   * This function moves the search element to the target container
   */
  //
  moveSearchElement(searchEle: HTMLElement, targetContainer: HTMLElement) {
    if (targetContainer && !targetContainer.contains(searchEle)) {
      this.removeBeaconModeClasses(searchEle);
      targetContainer.appendChild(searchEle);
    }
  }

  async handleScroll() {
    const header = this.shadowRoot?.querySelector(
      Constants.POP_SUMMARY_STICKY_HEADER_CONTAINER,
    );
    if (!(header instanceof HTMLElement)) {
      return;
    }
    const summaryComponent = document.querySelector(SummaryConstants.TAG_NAME);
    const summarySearchContainer = summaryComponent?.shadowRoot?.querySelector(
      Constants.SUMMARY_SEARCH_CONTAINER,
    );
    const headerSearchContainer = this.shadowRoot?.querySelector(
      Constants.SUMMARY_SEARCH_CONTAINER,
    );

    const shouldStick = window.scrollY > header.offsetTop;
    this.showTitleAndSearch = shouldStick;
    header.classList.toggle(Constants.STICKY, shouldStick);

    const searchEle = (
      shouldStick ? summarySearchContainer : headerSearchContainer
    )?.querySelector(SummarySearchConstants.TAG_NAME);

    if (
      searchEle instanceof HTMLElement &&
      headerSearchContainer instanceof HTMLElement &&
      summarySearchContainer instanceof HTMLElement
    ) {
      this.moveSearchElement(
        searchEle,
        shouldStick ? headerSearchContainer : summarySearchContainer,
      );
    }
    if (shouldStick) {
      const stickyTitleContainer = header.querySelector(
        Constants.STICKY_TITLE_CONTAINER,
      );
      this.tabBarPadding = `${stickyTitleContainer?.clientHeight}px`;
    } else {
      this.tabBarPadding = Constants.ZERO_PX;
    }
    this.applyBeaconNavigationPadding();
  }

  render() {
    return this.renderComponent(html`
      <div class="pop-summary-sticky-header-container">
        <div
          class="b-container sticky-title-search ${this.showTitleAndSearch
            ? 'show'
            : 'hide'}"
        >
          ${this.renderTitleAndSearch()}
        </div>
        <div class="sticky-navigation">
          <slot name="local-navigation"></slot>
        </div>
      </div>
    `);
  }
}
